import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { EXCLUDE_REFERENTI } from '../utils/contactPollution';
import { buildPhoneDigitPatterns } from '../utils/phoneSearch';
import type { SearchResult } from '../types';
import { useSettingsStore } from './settingsStore';
import { usePermissionsStore } from './permissionsStore';
import { leadSourceOrCondition } from '../utils/leadVisibility';

const PROJECT_STATUS_IT: Record<string, string> = {
  active: 'Attivo',
  inactive: 'Inattivo',
  completed: 'Completato',
  on_hold: 'In pausa',
  cancelled: 'Annullato',
  archived: 'Archiviato',
};

const DEAL_STATUS_IT: Record<string, string> = {
  pending: 'In attesa',
  sent: 'Inviata',
  accepted: 'Accettata',
  rejected: 'Rifiutata',
  won: 'Vinta',
  lost: 'Persa',
  draft: 'Bozza',
  cancelled: 'Annullata',
};

const TASK_STATUS_IT: Record<string, string> = {
  todo: 'Da fare',
  in_progress: 'In corso',
  done: 'Completato',
  completed: 'Completato',
  cancelled: 'Annullato',
};

const PRODUCT_STATUS_IT: Record<string, string> = {
  active: 'Attivo',
  inactive: 'Inattivo',
  discontinued: 'Discontinuato',
};

function t_project(status: string) { return PROJECT_STATUS_IT[status] ?? status; }
function t_deal(status: string) { return DEAL_STATUS_IT[status] ?? status; }
function t_task(status: string) { return TASK_STATUS_IT[status] ?? status; }
function t_product(status: string) { return PRODUCT_STATUS_IT[status] ?? status; }

interface SearchStore {
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  search: (query: string, userPermissions?: any) => Promise<void>;
}

export const useSearchStore = create<SearchStore>((set) => ({
  results: [],
  loading: false,
  error: null,

  search: async (query, userPermissions) => {
    if (!query || query.trim().length < 2) {
      set({ results: [], loading: false });
      return;
    }

    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();

      if (!userData?.user) {
        set({ results: [], loading: false, error: 'User not authenticated' });
        return;
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        set({ results: [], loading: false, error: 'User organization not found' });
        return;
      }

      const organizationId = userProfile.organization_id;

      // Get search settings and filter by user permissions
      const settings = useSettingsStore.getState().settings;
      const searchSettings = settings?.search_settings || {
        enabledEntities: {
          clients: true,
          projects: true,
          tasks: false,
          deals: true,
          inventory: false
        },
        maxResults: 10
      };

      // Override with user permissions - only search entities the user can view
      const enabledEntities = {
        // Lead e clienti stanno nella stessa tabella: chi ha solo il permesso Lead
        // (es. ruolo Advertiser) deve poter cercare i lead.
        clients: (searchSettings.enabledEntities?.clients ?? true) && ((userPermissions?.canViewClient || userPermissions?.canViewLead) ?? true),
        projects: (searchSettings.enabledEntities?.projects ?? true) && (userPermissions?.canViewProject ?? true),
        tasks: (searchSettings.enabledEntities?.tasks ?? false) && (userPermissions?.canViewProject ?? true), // tasks use project permission
        deals: (searchSettings.enabledEntities?.deals ?? true) && (userPermissions?.canViewDeal ?? true),
        inventory: (searchSettings.enabledEntities?.inventory ?? false) && (userPermissions?.canViewInventory ?? false)
      };

      const { maxResults } = searchSettings;
      const results: SearchResult[] = [];
      const clientIds: string[] = [];

      // Search clients and leads if enabled (same table, differentiated by is_lead in client_states)
      if (enabledEntities.clients) {
        // Read permissions directly from store (more reliable than parameter passing)
        const permStore = usePermissionsStore.getState();
        const shouldFilterClientsByUser =
          permStore.hasPermission('leads', 'filter_by_user') ||
          permStore.hasPermission('clients', 'filter_by_user');
        // Match sul telefono via phone_digits (solo cifre, country_code+phone): copre
        // spazi/trattini/parentesi nel valore salvato e il prefisso internazionale,
        // che ILIKE su `phone` letterale non trovava (vedi report AMG Educational).
        const phoneDigitCond = buildPhoneDigitPatterns(query)
          .map(p => `phone_digits.ilike.%${p}%`)
          .join(',');
        const searchCond = `business_name.ilike.%${query}%,trade_name.ilike.%${query}%,email.ilike.%${query}%${phoneDigitCond ? ',' + phoneDigitCond : ''}`;
        let clientQuery = supabase
          .from('clients')
          .select('id, business_name, trade_name, email, phone, entity_type, stato_cliente')
          .eq('organization_id', organizationId)
          .or(EXCLUDE_REFERENTI); // Fase 2: no persone-referente nella ricerca globale
        // Restrizioni di visibilità del ruolo (pipeline / fonti): la condizione
        // fonte/attribuzione va dentro l'unico .or() insieme alla ricerca.
        const leadVisibility = permStore.getLeadVisibility();
        if (leadVisibility?.pipelineIds) {
          clientQuery = clientQuery.in('pipeline_id', leadVisibility.pipelineIds.length > 0 ? leadVisibility.pipelineIds : ['00000000-0000-0000-0000-000000000000']);
        }
        const visibilityCond = leadSourceOrCondition(leadVisibility);
        const scopedSearchCond = visibilityCond ? `and(or(${visibilityCond}),or(${searchCond}))` : `or(${searchCond})`;
        if (shouldFilterClientsByUser) {
          const userId = userData.user.id;
          // Single .or() with nested and/or to avoid double .or() merging bug
          // (assigned_to=user AND search) OR (sales_rep=user AND search) = search AND (user match)
          clientQuery = clientQuery.or(
            `and(assigned_to.eq.${userId},${scopedSearchCond}),and(sales_rep_id.eq.${userId},${scopedSearchCond})`
          );
        } else {
          clientQuery = clientQuery.or(visibilityCond ? scopedSearchCond : searchCond);
        }
        const { data: clients, error: clientsError } = await clientQuery.limit(maxResults);

        if (clientsError) throw clientsError;

        // Collect client IDs for related searches
        if (clients) {
          clientIds.push(...clients.map(c => c.id));
        }

        // Get unique stato_cliente IDs to fetch client_states
        const statoClienteIds = clients?.map(c => c.stato_cliente).filter(Boolean) as string[] || [];
        const uniqueStatoClienteIds = [...new Set(statoClienteIds)];

        // Fetch client_states for all unique stato_cliente IDs
        let clientStatesMap: Record<string, { id: string; name: string; is_lead: boolean }> = {};
        if (uniqueStatoClienteIds.length > 0) {
          const { data: clientStates } = await supabase
            .from('client_states')
            .select('id, name, is_lead')
            .in('id', uniqueStatoClienteIds)
            .eq('organization_id', organizationId);

          if (clientStates) {
            clientStatesMap = clientStates.reduce((acc, state) => {
              acc[state.id] = state;
              return acc;
            }, {} as Record<string, { id: string; name: string; is_lead: boolean }>);
          }
        }

        results.push(...(clients?.map(client => {
          const clientState = client.stato_cliente ? clientStatesMap[client.stato_cliente] : undefined;
          return {
            type: clientState?.is_lead ? 'lead' as const : 'client' as const,
            id: client.id,
            title: client.business_name,
            subtitle: `${client.email || client.phone || ''} ${clientState?.is_lead ? '• Lead' : '• Cliente'}`,
            link: `/clients/${client.id}`,
          };
        }) || []));
      }

      // Direct search for projects if enabled
      if (enabledEntities.projects) {
        const { data: projects, error: projectsError } = await supabase
          .from('projects')
          .select(`
            id,
            name,
            description,
            status,
            client:clients (
              id,
              business_name
            )
          `)
          .eq('organization_id', organizationId)
          .eq('archived', false)
          .or(`name.ilike.%${query}%, description.ilike.%${query}%`)
          .limit(maxResults);

        if (projectsError) throw projectsError;

        results.push(...(projects?.map(project => ({
          type: 'project' as const,
          id: project.id,
          title: project.name,
          subtitle: `${project.client?.business_name || 'Cliente sconosciuto'} • ${t_project(project.status)}`,
          link: `/projects/${project.id}`,
          clientId: project.client?.id,
          clientName: project.client?.business_name
        })) || []));
      }

      // Search for projects associated with found clients
      if (enabledEntities.projects && clientIds.length > 0) {
        const { data: relatedProjects, error: relatedProjectsError } = await supabase
          .from('projects')
          .select(`
            id,
            name,
            description,
            status,
            client_id,
            client:clients (
              id,
              business_name
            )
          `)
          .eq('organization_id', organizationId)
          .eq('archived', false)
          .in('client_id', clientIds)
          .limit(maxResults);

        if (relatedProjectsError) throw relatedProjectsError;

        // Add only projects that are not already in results
        const existingProjectIds = new Set(results.filter(r => r.type === 'project').map(r => r.id));

        if (relatedProjects) {
          const newProjects = relatedProjects.filter(p => !existingProjectIds.has(p.id));
          results.push(...newProjects.map(project => ({
            type: 'project' as const,
            id: project.id,
            title: project.name,
            subtitle: `${project.client?.business_name || 'Cliente sconosciuto'} • ${t_project(project.status)}`,
            link: `/projects/${project.id}`,
            clientId: project.client?.id,
            clientName: project.client?.business_name
          })));
        }
      }

      // Direct search for tasks if enabled
      if (enabledEntities.tasks) {
        const { data: tasks, error: tasksError } = await supabase
          .from('tasks')
          .select(`
            id,
            title,
            description,
            status,
            project:projects (
              id,
              name
            )
          `)
          .eq('organization_id', organizationId)
          .or(`title.ilike.%${query}%, description.ilike.%${query}%`)
          .limit(maxResults);

        if (tasksError) throw tasksError;

        results.push(...(tasks?.map(task => ({
          type: 'task' as const,
          id: task.id,
          title: task.title,
          subtitle: `${task.project?.name || 'Nessun progetto'} • ${t_task(task.status)}`,
          link: `/projects/${task.project?.id}`,
        })) || []));
      }

      // Direct search for deals if enabled
      if (enabledEntities.deals) {
        let dealsQuery = supabase
          .from('deals')
          .select(`
            id,
            title,
            description,
            amount,
            status,
            client:clients (
              id,
              business_name
            )
          `)
          .eq('organization_id', organizationId)
          .or(`title.ilike.%${query}%, description.ilike.%${query}%`);
        if (userPermissions?.canFilterDealsByUser) {
          dealsQuery = dealsQuery.eq('assigned_to', userData.user.id);
        }
        const { data: deals, error: dealsError } = await dealsQuery.limit(maxResults);

        if (dealsError) throw dealsError;

        results.push(...(deals?.map(deal => ({
          type: 'deal' as const,
          id: deal.id,
          title: deal.title || 'Offerta senza titolo',
          subtitle: `${deal.client?.business_name || 'Cliente sconosciuto'} • €${(deal.amount ?? 0).toLocaleString('it-IT')} • ${t_deal(deal.status)}`,
          link: `/deals/${deal.id}`,
          clientId: deal.client?.id,
          clientName: deal.client?.business_name
        })) || []));
      }

      // Search for deals associated with found clients
      if (enabledEntities.deals && clientIds.length > 0) {
        let relatedDealsQuery = supabase
          .from('deals')
          .select(`
            id,
            title,
            description,
            amount,
            status,
            client_id,
            client:clients (
              id,
              business_name
            )
          `)
          .eq('organization_id', organizationId)
          .in('client_id', clientIds);
        if (userPermissions?.canFilterDealsByUser) {
          relatedDealsQuery = relatedDealsQuery.eq('assigned_to', userData.user.id);
        }
        const { data: relatedDeals, error: relatedDealsError } = await relatedDealsQuery.limit(maxResults);

        if (relatedDealsError) throw relatedDealsError;

        // Add only deals that are not already in results
        const existingDealIds = new Set(results.filter(r => r.type === 'deal').map(r => r.id));

        if (relatedDeals) {
          const newDeals = relatedDeals.filter(d => !existingDealIds.has(d.id));
          results.push(...newDeals.map(deal => ({
            type: 'deal' as const,
            id: deal.id,
            title: deal.title || 'Offerta senza titolo',
            subtitle: `${deal.client?.business_name || 'Cliente sconosciuto'} • €${(deal.amount ?? 0).toLocaleString('it-IT')} • ${t_deal(deal.status)}`,
            link: `/deals/${deal.id}`,
            clientId: deal.client?.id,
            clientName: deal.client?.business_name
          })));
        }
      }

      // Search inventory/products if enabled
      if (enabledEntities.inventory) {
        const { data: products, error: productsError } = await supabase
          .from('products')
          .select(`
            id,
            name,
            sku,
            status
          `)
          .eq('organization_id', organizationId)
          .or(`name.ilike.%${query}%, sku.ilike.%${query}%`)
          .limit(maxResults);

        if (productsError) throw productsError;

        results.push(...(products?.map(product => ({
          type: 'product' as const,
          id: product.id,
          title: product.name,
          subtitle: product.sku ? `SKU: ${product.sku} • ${t_product(product.status)}` : t_product(product.status),
          link: `/magazzino/prodotti/${product.id}`,
        })) || []));
      }

      set({
        results,
        loading: false
      });
    } catch (error) {
      console.error('Search error:', error);
      set({ 
        error: friendlyDbError(error), 
        loading: false,
        results: [] 
      });
    }
  },
}));